import { useEffect, useRef, useState } from "react";
import { Field, Fieldset } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import FormInput from "../../Form/FormInput";
import FormSelect from "../../Form/FormSelect";
import FormRadioGroup from "../../Form/FormRadioGroup";
import FormError from "../../Form/FormError";
import Button from "../../Button";
import SlideOver from "../../Modal/SlideOver";
import { isBusinessWriteBlocked } from "../../../../Constants/business";
import WriteBlockGate from "../../Business/WriteBlockGate";
import {
  addLocalNotification,
  clearBusinessProduct,
  clearBusinessProductError,
  requestCreateBusinessProduct,
  requestCreateProductCategory,
  requestListProductCategories,
  requestUpdateBusinessProduct,
  useAuthState,
  useBusinessProductDispatch,
  useBusinessProductState,
  useNotificationDispatch,
} from "../../../../Context";
import { PRODUCT_TYPES, TAX_BASE_TYPES } from "../../../../Constants/products";
import TaxBaseField from "../TaxBaseField";
import { cancelAPIRequest } from "../../../../Utils";

const ProductModal = ({ open, onClose, product }) => {
  const { t } = useTranslation();

  const [name, setName] = useState("");
  const [category, setCategory] = useState(null);
  const [price, setPrice] = useState("");
  const [cost, setCost] = useState("");
  const [reference, setReference] = useState("");
  const [errors, setErrors] = useState({});
  const [productType, setProductType] = useState(null);
  const [taxBaseType, setTaxBaseType] = useState(null);
  const [taxBaseValue, setTaxBaseValue] = useState("");
  const [taxBaseNumerator, setTaxBaseNumerator] = useState("");
  const [taxBaseDenominator, setTaxBaseDenominator] = useState("");

  const dispatch = useBusinessProductDispatch();
  const notificationDispatch = useNotificationDispatch();

  const { user, getUserLoading: userLoading } = useAuthState();
  const writeBlocked = isBusinessWriteBlocked(user?.mainBusiness);
  const {
    request,
    productCategories,
    productCategoriesLoading,
    productCategoriesError,
    newCategoryLoading,
    newCategoryError,
    newProduct,
    newProductLoading,
    newProductError,
    productID: editProduct,
    updateProductLoading: editProductLoading,
    updateProductError: editProductError,
  } = useBusinessProductState();

  const isEdit = Boolean(product);
  const submitDisabled =
    userLoading || productCategoriesLoading || newCategoryLoading;
  const submitLoading = newProductLoading || editProductLoading;

  const categories = productCategories.map((category) => ({
    ...category,
    label: category.name,
    value: category.id,
  }));

  // Clean up on unmount only. With `request` as a dependency this cleanup also
  // ran when a failed save reset `request` to null, wiping the error before it
  // was shown, and cancelled whatever request the page had in flight.
  const requestRef = useRef(request);
  requestRef.current = request;
  useEffect(() => {
    return () => {
      cancelAPIRequest(requestRef.current);
      clearBusinessProduct(dispatch);
      clearBusinessProductError(dispatch);
    };
  }, [dispatch]);

  // Load categories
  useEffect(() => {
    if (user?.mainBusiness?.id) {
      requestListProductCategories(dispatch, {
        businessID: user.mainBusiness.id,
      });
    }
  }, [dispatch, user?.mainBusiness?.id]);

  // Populate form when editing
  useEffect(() => {
    if (product) {
      setName(product.name);
      setPrice(product.price.toString());
      setCost(product.cost?.toString() || "");
      setReference(product.reference || "");
      setProductType(PRODUCT_TYPES.find((type) => type.value === product.type));
      if (product.taxBaseType) {
        setTaxBaseType(
          TAX_BASE_TYPES.find((type) => type.value === product.taxBaseType),
        );
        if (product.taxBaseType === "DECIMAL") {
          setTaxBaseValue(product.taxBaseRate || "");
        }
        if (product.taxBaseType === "PERCENTAGE") {
          setTaxBaseValue(product.taxBasePercentage || "");
        }
        if (product.taxBaseType === "FRACTION") {
          setTaxBaseNumerator(product.taxBaseFractionNumerator || "");
          setTaxBaseDenominator(product.taxBaseFractionDenumerator || "");
        }
      }
    }
  }, [product]);

  // Populate selected category when editing
  useEffect(() => {
    if (Boolean(product?.category?.id) && productCategories.length > 0) {
      const selectedCategory = productCategories.find(
        (category) => category.id === product.category.id,
      );
      if (selectedCategory?.id) {
        setCategory({
          ...selectedCategory,
          value: selectedCategory.id,
          label: selectedCategory.name,
        });
      }
    }
  }, [product?.category?.id, productCategories]);

  //  Handle create success
  useEffect(() => {
    if (newProduct) {
      // Close modal
      onClose();
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `create-product-${newProduct}`,
        type: "success",
        title: "common.success",
        body: "products.notifications.createSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, newProduct, onClose]);

  //  Handle update success
  useEffect(() => {
    if (editProduct) {
      // Close modal
      onClose();
      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `edit-product-${editProduct}`,
        type: "success",
        title: "common.success",
        body: "products.notifications.updateSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, editProduct, onClose]);

  const clearError = (field) =>
    setErrors((prev) => ({ ...prev, [field]: undefined }));

  const handleFieldChange = (setter, field) => (eventOrValue) => {
    const value =
      eventOrValue?.target !== undefined
        ? eventOrValue.target.value
        : eventOrValue;
    setter(value);
    clearError(field);
  };

  const handleNameChange = (e) => {
    const newValue = e.target.value;
    if (newValue.length <= 200) {
      setName(newValue);
      if (newValue) clearError("name");
    }
  };
  const validate = () => {
    const newErrors = {};

    if (!name) {
      newErrors.name = "products.errors.nameRequired";
    }

    if (!productType?.value) {
      newErrors.productType = "products.errors.typeRequired";
    }

    if (!price) {
      newErrors.price = "products.errors.priceRequired";
    }

    if (taxBaseType?.value === "DECIMAL" && !taxBaseValue) {
      newErrors.taxBaseValue = "products.errors.taxBaseValueRequired";
    }

    if (taxBaseType?.value === "PERCENTAGE" && !taxBaseValue) {
      newErrors.taxBaseValue = "products.errors.taxBaseValueRequired";
    }

    if (
      taxBaseType?.value === "FRACTION" &&
      (!taxBaseNumerator || !taxBaseDenominator)
    ) {
      newErrors.taxBaseNumerator = "products.errors.taxBaseNumeratorRequired";
      newErrors.taxBaseDenominator =
        "products.errors.taxBaseDenominatorRequired";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) return;

    saveProduct();
  };

  const saveProduct = () => {
    const input = {
      id: isEdit ? product.id : undefined,
      businessID: user.mainBusiness.id,
      name,
      type: productType.value,
      price: Number(price.replace(/\D/g, "")),
      cost: cost ? Number(cost.replace(/\D/g, "")) : undefined,
      categoryID: category?.value ? category.value : undefined,
      reference: reference ? reference : undefined,
      taxBaseType: null,
      taxBaseRate: null,
      taxBasePercentage: null,
      taxBaseFractionNumerator: null,
      taxBaseFractionDenumerator: null,
    };

    if (taxBaseType?.value === "DECIMAL" && taxBaseValue) {
      input.taxBaseType = taxBaseType?.value;
      input.taxBaseRate = taxBaseValue;
    }

    if (taxBaseType?.value === "PERCENTAGE" && taxBaseValue) {
      input.taxBaseType = taxBaseType?.value;
      input.taxBasePercentage = taxBaseValue;
    }

    if (
      taxBaseType?.value === "FRACTION" &&
      taxBaseNumerator &&
      taxBaseDenominator
    ) {
      input.taxBaseType = taxBaseType?.value;
      input.taxBaseFractionNumerator = taxBaseNumerator;
      input.taxBaseFractionDenumerator = taxBaseDenominator;
    }

    if (isEdit) {
      requestUpdateBusinessProduct(dispatch, { input });
    } else {
      requestCreateBusinessProduct(dispatch, { input });
    }
  };

  const createCategory = (categoryName) => {
    const input = {
      businessID: user.mainBusiness.id,
      name: categoryName,
    };
    requestCreateProductCategory(dispatch, { input });
  };

  const calculateTaxBaseResult = () => {
    const basePrice = Number(price.replace(/\D/g, "")) || 0;

    if (!taxBaseType) return "";

    switch (taxBaseType.value) {
      case "DECIMAL":
        return (basePrice * (Number(taxBaseValue) || 0)).toFixed(0);

      case "PERCENTAGE":
        return ((basePrice * (Number(taxBaseValue) || 0)) / 100).toFixed(0);

      case "FRACTION":
        const numerator = Number(taxBaseNumerator) || 0;
        const denominator = Number(taxBaseDenominator) || 1;
        return (basePrice * (numerator / denominator)).toFixed(0);

      default:
        return "";
    }
  };

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={isEdit ? "products.modal.title.edit" : "products.modal.title.add"}
      footer={
        <div className="flex justify-end space-x-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={writeBlocked || submitDisabled}
            loading={submitLoading}
            type="submit"
            form="new-product-form"
            variant="primary"
          >
            {isEdit ? t("common.edit") : t("common.create")}
          </Button>
        </div>
      }
    >
      <form
        id="new-product-form"
        className="space-y-4 mb-6"
        onSubmit={handleSubmit}
      >
        <WriteBlockGate>
        <FormError error={newProductError || editProductError} />
        <Fieldset
          disabled={userLoading || submitLoading}
          className="space-y-4 data-[disabled]:opacity-50"
        >
          <Field>
            <FormInput
              required
              label="products.fields.name"
              id="product-name"
              name="product_name"
              value={name}
              onChange={handleNameChange}
              onClear={() => setName("")}
              placeholder={t("products.placeholders.name")}
              error={errors.name}
            />
          </Field>

          <FormRadioGroup
            required
            label="products.fields.type"
            name="type"
            options={PRODUCT_TYPES}
            value={productType}
            onChange={handleFieldChange(setProductType, "productType")}
            gridClassName="grid-cols-1 sm:grid-cols-2"
            error={errors.productType}
          />

          <Field>
            <FormSelect
              label="products.fields.category"
              id="category"
              name="category"
              searchable
              options={categories}
              allowAdd
              onAdd={createCategory}
              value={category}
              onChange={setCategory}
              isLoading={productCategoriesLoading}
              isAdding={newCategoryLoading}
              addError={newCategoryError}
              placeholder={t("products.placeholders.category")}
              error={errors.category || productCategoriesError}
            />
          </Field>

          <Field>
            <FormInput
              required
              label="products.fields.price"
              id="price"
              name="price"
              value={price}
              onChange={handleFieldChange(setPrice, "price")}
              onClear={() => setPrice("")}
              placeholder={t("products.placeholders.price")}
              numberFormat={{
                thousandSeparator: true,
              }}
              prefix="Rp"
              error={errors.price}
            />
          </Field>

          <TaxBaseField
            type={taxBaseType}
            onTypeChange={setTaxBaseType}
            value={taxBaseValue}
            onValueChange={setTaxBaseValue}
            numerator={taxBaseNumerator}
            onNumeratorChange={setTaxBaseNumerator}
            denominator={taxBaseDenominator}
            onDenominatorChange={setTaxBaseDenominator}
            result={calculateTaxBaseResult()}
            errors={errors}
          />

          <Field>
            <FormInput
              hint="common.optional"
              label="products.fields.cost"
              id="cost"
              name="cost"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              onClear={() => setCost("")}
              placeholder={t("products.placeholders.cost")}
              numberFormat={{
                thousandSeparator: true,
              }}
              prefix="Rp"
              error={errors.cost}
            />
          </Field>

          <Field>
            <FormInput
              hint="common.optional"
              label="products.fields.reference"
              id="reference"
              name="reference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              onClear={() => setCost("")}
              placeholder={t("products.placeholders.reference")}
              error={errors.reference}
            />
          </Field>
        </Fieldset>
        </WriteBlockGate>
      </form>
    </SlideOver>
  );
};

export default ProductModal;
